import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { PairedIcp } from '@uigy/generated/graphql';
import { IEM_MACHINE_ID } from '@uigy/shared/constants/iem.constants';
import { MenuItem, NavigationTree } from '@uigy/shared/models/interfaces';
import { IemService } from '@uigy/shared/services/iem.service';
import { orderBy } from 'lodash-es';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { NavigationRoutesService } from '@uigy/core/modules/layout/navigation-routes.service';
import Utils from '@uigy/utils';
import { LanguageService } from '@uigy/shared/services/language.service';
import {
  SavedFilterListItem,
  SavedFiltersService,
} from '@uigy/shared/components/ui-grid/components/filters/saved-filters/saved-filters.service';
import { IconsEnum } from '@uigy/generated/icons.enum';
import {
  SaveFiltersFieldNames
} from '@uigy/shared/components/ui-grid/components/filters/saved-filters/save-filter-form/interfaces';
import { RoutesStatesNamesEnum } from '@uigy/shared/enums/routesStatesNames.enum';
import { InventoryTabsComponent } from '@uigy/modules/inventory/components/inventory-tabs/inventory-tabs.component';
import { FindingsTabQueryParams, InventoryTabQueryParams, TabGroup } from '@uigy/shared/components/tabs/tabs.enums';

interface IEMDDItem {
  id: number;
  name: string;
  value: string;
}

@Component({
    selector: 'app-navigation',
    templateUrl: './navigation.component.html',
    styleUrls: ['./navigation.component.scss'],
    standalone: false
})

export class NavigationComponent implements OnInit, OnDestroy {
  public iemDDData: IEMDDItem[] = [{
    id: 0,
    name: this.languageService.texts.general.key_words.em,
    value: IEM_MACHINE_ID,
  }];
  public mySavedViewsTexts = this.languageService.texts.my_saved_views;
  public machineId$ = this.iemService.machineId$;
  public treeData: NavigationTree | null = null;
  private destroy$ = new Subject<void>();

  // My Views properties
  protected readonly IconsEnum = IconsEnum;
  public savedFiltersMenuItems = signal<MenuItem[]>([]);
  public isMyViewsMenuOpen = signal(false);

  constructor(
    public iemService: IemService,
    private navigationRoutesService: NavigationRoutesService,
    private languageService: LanguageService,
    private savedFiltersService: SavedFiltersService,
    private router: Router,
  ) {
  }

  public onItemClicked(machineId: string): void {
    this.iemService.machineId = machineId;
  }

  public ngOnInit(): void {
    this.loadSavedFilters();

    // Listen for filter changes and reload
    this.savedFiltersService.onFiltersChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadSavedFilters());

    this.machineId$.pipe(takeUntil(this.destroy$)).subscribe(machineId =>
      this.treeData = this.navigationRoutesService.getTreeData(this.iemService.isIEM() && machineId === IEM_MACHINE_ID),
    );

    this.iemService.getIemDetails().pipe(take(1)).subscribe(() => {
      this.initDropdown();
    });

    // Close My Views menu on navigation
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.closeMyViewsMenu();
      });

  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public toggleMyViewsMenu(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.isMyViewsMenuOpen.set(!this.isMyViewsMenuOpen());
  }

  public closeMyViewsMenu(): void {
    this.isMyViewsMenuOpen.set(false);
  }

  private loadSavedFilters(): void {
    this.savedFiltersService.getAllSavedFilters()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (filters) => {
          const menuItems: MenuItem[] = filters.map(filter => ({
            label: this.getSavedViewItemTitle(filter),
            value: filter,
            action: () => this.savedViewItemAction(filter)
          }));
          this.savedFiltersMenuItems.set(menuItems);
        },
        error: (error) => {
          this.savedFiltersMenuItems.set([]);
        }
      });
  }

  private initDropdown(): void {
    if (this.iemService.isIEM() && this.iemService.iemPairedIcps) {
      this.iemDDData = this.iemDDData.concat(
        orderBy(Utils.stripConnection(this.iemService.iemPairedIcps), 'name', 'asc').map((icp: PairedIcp, index: number): IEMDDItem => ({
          id: index + 1,
          name: icp.site.name ? icp.site.name : (index + 1).toString(),
          value: icp.site.machineId,
        })));
    }
  }

  private getSavedViewItemTitle(filter: SavedFilterListItem): string {
    const fieldTitleMap: Record<SaveFiltersFieldNames, string> = {
      [SaveFiltersFieldNames.AssetField]: this.mySavedViewsTexts.inventory,
      [SaveFiltersFieldNames.AssetFieldControllers]: this.mySavedViewsTexts.controllers,
      [SaveFiltersFieldNames.AssetFieldIOT]: this.mySavedViewsTexts.iot,
      [SaveFiltersFieldNames.AssetFieldNetwork]: this.mySavedViewsTexts.network,
      [SaveFiltersFieldNames.FindingField]: this.mySavedViewsTexts.vulnerabilities,
      [SaveFiltersFieldNames.PolicyFindingFilterField]: this.mySavedViewsTexts.policy_violations,
    };

    const categoryTitle = fieldTitleMap[filter.field as SaveFiltersFieldNames];
    return categoryTitle ? `${filter.name} (${categoryTitle})` : filter.name;
  }

  private savedViewItemAction(filter: SavedFilterListItem): void {
    let route: RoutesStatesNamesEnum[] = [];
    let params: any;

    switch (filter.field) {
      case SaveFiltersFieldNames.AssetField:
        route = [RoutesStatesNamesEnum.inventory];
        params = { ...InventoryTabsComponent.defaultNavigation };
        break;
      case SaveFiltersFieldNames.AssetFieldControllers:
        route = [RoutesStatesNamesEnum.inventory];
        params = { [TabGroup.InventoryTab]: InventoryTabQueryParams.ControllersAssets };
        break;
      case SaveFiltersFieldNames.AssetFieldIOT:
        route = [RoutesStatesNamesEnum.inventory];
        params = { [TabGroup.InventoryTab]: InventoryTabQueryParams.IotAssets };
        break;
      case SaveFiltersFieldNames.AssetFieldNetwork:
        route = [RoutesStatesNamesEnum.inventory];
        params = { [TabGroup.InventoryTab]: InventoryTabQueryParams.NetworkAssets };
        break;
      case SaveFiltersFieldNames.FindingField:
        route = [RoutesStatesNamesEnum.risks, RoutesStatesNamesEnum.findings];
        params = { [TabGroup.FindingsTab]: FindingsTabQueryParams.Vulnerabilities };
        break;
      case SaveFiltersFieldNames.PolicyFindingFilterField:
        route = [RoutesStatesNamesEnum.risks, RoutesStatesNamesEnum.findings];
        params = { [TabGroup.FindingsTab]: FindingsTabQueryParams.PolicyViolations };
        break;
    }

    this.navigationRoutesService.navigateWithParams(route, {
      ...params,
      savedFiltersFieldName: filter.field,
      activeSavedFilterName: filter.name,
    });

    this.closeMyViewsMenu();
  }

}
